import React, { createContext, useContext, useState, useEffect } from 'react';

const GlobalFontSizeContext = createContext();

const STORAGE_KEY = 'bloom_font_size';
const DEFAULT_FONT_SIZE = 16;
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 24;
const FONT_SIZE_STEP = 2;

/**
 * Global Font Size Context
 * Provides global font size control for the entire application
 * Persists font size preference in localStorage
 */
export const GlobalFontSizeProvider = ({ children }) => {
  const [fontSize, setFontSize] = useState(() => {
    // Initialize from localStorage if available
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed) && parsed >= MIN_FONT_SIZE && parsed <= MAX_FONT_SIZE) {
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error reading font size from localStorage:', error);
    }
    return DEFAULT_FONT_SIZE;
  });

  /**
   * Publish the setting to CSS. Runs on mount too (no separate mount effect),
   * since the initial state already carries the persisted value.
   *
   * `--font-scale` is the lever that reaches text we never hand-wired: every
   * authored `font-size` is multiplied by it at build time (see
   * postcss/scalable-font-size.js), so the hard-coded `text-[14px]` utilities
   * scale alongside the `style={{ fontSize }}` call sites.
   *
   * The root `font-size` is deliberately left alone. Setting it used to be the
   * only global lever, but Tailwind v4 derives `--spacing`, `--container-*` and
   * `--radius-*` from rem — so it rescaled ~2600 padding/gap/width utilities
   * (layout drift) while leaving the px-sized text untouched. Text-only scaling
   * is also what docs/portrait-signage-standard.md asks of this control.
   */
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--global-font-size', `${fontSize}px`);
    root.style.setProperty('--font-scale', `${fontSize / DEFAULT_FONT_SIZE}`);

    try {
      localStorage.setItem(STORAGE_KEY, fontSize.toString());
    } catch (error) {
      console.error('Error saving font size to localStorage:', error);
    }
  }, [fontSize]);

  const increase = () => {
    setFontSize((prev) => Math.min(prev + FONT_SIZE_STEP, MAX_FONT_SIZE));
  };

  const decrease = () => {
    setFontSize((prev) => Math.max(prev - FONT_SIZE_STEP, MIN_FONT_SIZE));
  };

  return (
    <GlobalFontSizeContext.Provider value={{ fontSize, increase, decrease }}>
      {children}
    </GlobalFontSizeContext.Provider>
  );
};

export const useGlobalFontSize = () => {
  const context = useContext(GlobalFontSizeContext);
  if (!context) {
    // Return default values if used outside provider
    return { fontSize: DEFAULT_FONT_SIZE, increase: () => {}, decrease: () => {} };
  }
  return context;
};

export default GlobalFontSizeContext;

